import { useState, useEffect, useCallback } from 'react';
import { safeJson, networkMessage } from '../apiHelpers';
import ReviewBox, { VerifiedBadge } from './ReviewBox';
import { t } from '../i18n';
import PiPrice from './PiPrice';
import MapPickerModal from './MapPickerModal';
import TravelMapView from './TravelMapView';

const API_BASE = '/api/travel';

function getAuthToken() {
  return localStorage.getItem('token') || '';
}

function authHeaders() {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Token ${token}` } : {}),
  };
}

const colors = {
  bg: '#F7F5F0',
  surface: '#fff',
  border: '#E3E1D9',
  text: '#1F1E1B',
  textMuted: '#5F5E5A',
  primary: '#0F6E56',
  primaryBg: '#E1F5EE',
  danger: '#A32D2D',
};

const TYPES = [
  ['hotel', 'Hotel'], ['apartment', 'Apartment'], ['room', 'Private Room'],
  ['villa', 'Villa'], ['hostel', 'Hostel'], ['guesthouse', 'Guesthouse'],
];

function ListingCard({ listing, onOpen, piRate }) {
  return (
    <div onClick={() => onOpen(listing.slug)} style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 12, overflow: 'hidden', cursor: 'pointer' }}>
      <div style={{ width: '100%', aspectRatio: '4/3', background: '#F1EFE8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {listing.cover_image ? (
          <img src={listing.cover_image} alt={listing.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <span style={{ fontSize: 32, opacity: 0.3 }}>🏨</span>
        )}
      </div>
      <div style={{ padding: '0.65rem' }}>
        {listing.is_boosted && (
          <span style={{ fontSize: 10, background: '#FFF3C4', color: '#8A6D00', padding: '2px 6px', borderRadius: 8, marginBottom: 4, display: 'inline-block' }}>📢 {t('tv_sponsored')}</span>
        )}
        {listing.matched_interest && (
          <span style={{ fontSize: 10, background: colors.primaryBg, color: colors.primary, padding: '2px 6px', borderRadius: 8, marginBottom: 4, marginLeft: 4, display: 'inline-block' }}>✨ {t('tv_for_you')}</span>
        )}
        <p style={{ fontSize: 13, fontWeight: 500, margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{listing.title}</p>
        <p style={{ fontSize: 12, color: colors.textMuted, margin: '0 0 6px' }}>{listing.city}{listing.country ? `, ${listing.country}` : ''}</p>
        {listing.avg_rating > 0 && (
          <span style={{ fontSize: 11, color: colors.textMuted }}>⭐ {listing.avg_rating} ({listing.total_reviews})</span>
        )}
        <p style={{ fontSize: 14, fontWeight: 600, color: colors.primary, margin: '4px 0 0' }}>
          <PiPrice pi={listing.price_per_night_pi} suffix="" /> <span style={{ fontSize: 11, fontWeight: 400, color: colors.textMuted }}>/ {t('tv_night')}</span>
        </p>
      </div>
    </div>
  );
}

function ListingDetailModal({ slug, onClose, onBooked }) {
  const [listing, setListing] = useState(null);
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState(1);
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [booking, setBooking] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    fetch(`${API_BASE}/listings/${slug}/`).then((r) => r.json()).then(setListing).catch(() => {});
  }, [slug]);

  const nights = checkIn && checkOut ? Math.max(0, (new Date(checkOut) - new Date(checkIn)) / 86400000) : 0;
  const discountPercent = listing ? (nights >= 28 && listing.monthly_discount_percent ? listing.monthly_discount_percent : nights >= 7 && listing.weekly_discount_percent ? listing.weekly_discount_percent : 0) : 0;
  const basePrice = listing ? nights * listing.price_per_night_pi : 0;
  const total = listing ? (basePrice * (1 - discountPercent / 100)).toFixed(2) : 0;

  const placeBooking = async () => {
    if (!getAuthToken()) { setMsg(t('tv_login')); return; }
    if (!checkIn || !checkOut || nights <= 0) { setMsg(t('tv_dates')); return; }
    if (!window.Pi) { setMsg(t('tv_open_pi')); return; }
    setBooking(true);
    setMsg('');
    try {
      await window.Pi.authenticate(['username', 'payments'], () => {});
      const res = await fetch(`${API_BASE}/bookings/create/`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ listing_id: listing.id, check_in: checkIn, check_out: checkOut, guests_count: guests, guest_phone: phone, notes }),
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not create booking.');

      window.Pi.createPayment(
        { amount: data.price_pi, memo: `Booking: ${listing.title}`, metadata: { booking_id: data.booking_id } },
        {
          onReadyForServerApproval: (paymentId) => {
            fetch(`${API_BASE}/bookings/${data.booking_id}/approve/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ payment_id: paymentId }) });
          },
          onReadyForServerCompletion: (paymentId, txid) => {
            fetch(`${API_BASE}/bookings/${data.booking_id}/complete/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ payment_id: paymentId, txid }) })
              .then(() => { setMsg('Booking confirmed! Check "My Bookings".'); setBooking(false); onBooked(); })
              .catch(() => { setMsg('Payment reached Pi but booking activation failed.'); setBooking(false); });
          },
          onCancel: () => { setMsg(t('tv_cancelled')); setBooking(false); },
          onError: () => { setMsg(t('tv_pay_err')); setBooking(false); },
        }
      );
    } catch (e) {
      setMsg(e.message);
      setBooking(false);
    }
  };

  const addToCart = async () => {
    if (!getAuthToken()) { setMsg(t('tv_login')); return; }
    if (!checkIn || !checkOut || nights <= 0) { setMsg('Please select valid check-in/check-out dates first.'); return; }
    try {
      await fetch(`/api/cart/add/`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          app_name: 'travel', item_type: 'listing', reference_id: listing.id,
          title: listing.title, thumbnail_url: listing.cover_image || '', price_pi: parseFloat(total),
          metadata: { check_in: checkIn, check_out: checkOut, guests_count: guests, guest_phone: phone, notes },
        }),
      });
      setMsg('Added to cart! View it from the 🛒 My Cart menu.');
    } catch (e) {
      setMsg(t('tv_cart_fail'));
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px 12px', overflowY: 'auto' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, maxWidth: 560, width: '100%', padding: '1.5rem', marginTop: 20 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: colors.textMuted }}>✕</button>
        </div>
        {!listing ? <p style={{ textAlign: 'center', color: colors.textMuted }}>{t('loading')}</p> : (
          <>
            {listing.cover_image && <img src={listing.cover_image} alt={listing.title} style={{ width: '100%', borderRadius: 10, marginBottom: 8 }} />}
            {listing.gallery?.length > 0 && (
              <div style={{ display: 'flex', gap: 6, overflowX: 'auto', marginBottom: 12 }}>
                {listing.gallery.map((url, i) => (
                  <img key={i} src={url} alt="" style={{ width: 70, height: 70, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
                ))}
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h2 style={{ fontSize: 18, margin: '0 0 4px' }}>{listing.title}</h2>
              <VerifiedBadge module="travel" objectId={listing.id} />
            </div>
            <ReviewBox
              module="travel"
              objectId={listing.id}
              objectTitle={listing.title}
              isAdmin={localStorage.getItem('username') === 'khalidxpert'}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <p style={{ fontSize: 13, color: colors.textMuted, margin: 0 }}>{listing.city}, {listing.country} · Hosted by @{listing.host_username}</p>
              <a href={`/chat/${listing.host_username}`} style={{ fontSize: 12, fontWeight: 600, color: colors.primary, textDecoration: 'none', whiteSpace: 'nowrap', marginLeft: 8 }}>💬 {t('message_host')}</a>
            </div>
            <p style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 12 }}>{listing.description}</p>
            <p style={{ fontSize: 13, color: colors.textMuted, marginBottom: 12 }}>
              {listing.max_guests} guests · {listing.bedrooms} bedrooms · {listing.bathrooms} bathrooms
            </p>
            {listing.amenities?.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
                {listing.amenities.map((a, i) => (
                  <span key={i} style={{ fontSize: 11, background: colors.primaryBg, color: colors.primary, padding: '3px 8px', borderRadius: 10 }}>{a}</span>
                ))}
              </div>
            )}

            <div style={{ background: '#F1EFE8', borderRadius: 10, padding: '0.75rem', marginBottom: 16 }}>
              <p style={{ fontSize: 13, fontWeight: 600, margin: '0 0 6px' }}>🏠 {t('tv_rules')}</p>
              <p style={{ fontSize: 12, color: colors.textMuted, margin: '0 0 4px' }}>Check-in: {listing.check_in_time} · Check-out: {listing.check_out_time}</p>
              <p style={{ fontSize: 12, margin: '0 0 4px' }}>
                {listing.smoking_allowed ? '🚬 Smoking allowed' : '🚭 No smoking'} · {listing.pets_allowed ? '🐾 Pets allowed' : '🚫 No pets'} · {listing.parties_allowed ? '🎉 Parties allowed' : '🚫 No parties'}
              </p>
              {listing.house_rules && <p style={{ fontSize: 12, color: colors.textMuted, margin: 0 }}>{listing.house_rules}</p>}
            </div>

            <p style={{ fontSize: 16, fontWeight: 600, color: colors.primary, marginBottom: 4 }}><PiPrice pi={listing.price_per_night_pi} suffix=" / night" /></p>
            <p style={{ fontSize: 12, color: colors.textMuted, marginBottom: 12 }}>
              📋 {listing.cancellation_policy_display}
              {(listing.weekly_discount_percent > 0 || listing.monthly_discount_percent > 0) && (
                <span> · 💰 {listing.weekly_discount_percent > 0 && `${listing.weekly_discount_percent}% off 7+ nights`}{listing.weekly_discount_percent > 0 && listing.monthly_discount_percent > 0 && ', '}{listing.monthly_discount_percent > 0 && `${listing.monthly_discount_percent}% off 28+ nights`}</span>
              )}
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
              <div>
                <label style={{ fontSize: 12, color: colors.textMuted, display: 'block', marginBottom: 4 }}>{t('check_in')}</label>
                <input type="date" value={checkIn} onChange={(e) => setCheckIn(e.target.value)} style={{ width: '100%', padding: '8px', fontSize: 13, border: `0.5px solid ${colors.border}`, borderRadius: 6, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: colors.textMuted, display: 'block', marginBottom: 4 }}>{t('check_out')}</label>
                <input type="date" value={checkOut} onChange={(e) => setCheckOut(e.target.value)} style={{ width: '100%', padding: '8px', fontSize: 13, border: `0.5px solid ${colors.border}`, borderRadius: 6, boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 12, color: colors.textMuted, display: 'block', marginBottom: 4 }}>{t('guests')}</label>
              <input type="number" min="1" max={listing.max_guests} value={guests} onChange={(e) => setGuests(e.target.value)} style={{ width: '100%', padding: '8px', fontSize: 13, border: `0.5px solid ${colors.border}`, borderRadius: 6, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 12, color: colors.textMuted, display: 'block', marginBottom: 4 }}>{t('your_phone')}</label>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} style={{ width: '100%', padding: '8px', fontSize: 13, border: `0.5px solid ${colors.border}`, borderRadius: 6, boxSizing: 'border-box' }} />
            </div>
            <textarea placeholder={t('notes_for_host')} value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} style={{ width: '100%', padding: '8px', fontSize: 13, border: `0.5px solid ${colors.border}`, borderRadius: 6, boxSizing: 'border-box', marginBottom: 12, resize: 'vertical' }} />

            {nights > 0 && (
              <div style={{ marginBottom: 12 }}>
                <p style={{ fontSize: 13, color: colors.textMuted, margin: 0 }}>{nights} night{nights !== 1 ? 's' : ''} × <PiPrice pi={listing.price_per_night_pi} /> = <PiPrice pi={basePrice.toFixed(2)} /></p>
                {discountPercent > 0 && <p style={{ fontSize: 13, color: colors.primary, margin: '2px 0' }}>🎉 {discountPercent}% long-stay discount applied</p>}
                <p style={{ fontSize: 14, fontWeight: 600, margin: '4px 0 0' }}>Total: <PiPrice pi={total} /></p>
              </div>
            )}

            <button onClick={addToCart} style={{ width: '100%', padding: '12px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>
              🛒 Add to Cart{nights > 0 ? ` - $<PiPrice pi={total} />` : ''}
            </button>
            {msg && <p style={{ fontSize: 12, color: colors.danger, marginTop: 8 }}>{msg}</p>}

            {listing.reviews?.length > 0 && (
              <div style={{ marginTop: 16, borderTop: `0.5px solid ${colors.border}`, paddingTop: 12 }}>
                <p style={{ fontSize: 13, fontWeight: 600, margin: '0 0 8px' }}>{t('reviews')}</p>
                {listing.reviews.map((r, i) => (
                  <p key={i} style={{ fontSize: 12, color: colors.textMuted, margin: '4px 0' }}>@{r.reviewer} {'⭐'.repeat(r.rating)} {r.comment}</p>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function CreateListingForm({ onCreated }) {
  const [form, setForm] = useState({ title: '', property_type: 'apartment', description: '', price_per_night_pi: '', city: '', country: '', address: '', max_guests: 2, bedrooms: 1, bathrooms: 1, amenities: '', cancellation_policy: 'moderate', weekly_discount_percent: 0, monthly_discount_percent: 0, check_in_time: '3:00 PM', check_out_time: '11:00 AM', smoking_allowed: false, pets_allowed: false, parties_allowed: false, house_rules: '' });
  const [imageFile, setImageFile] = useState(null);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const inputStyle = { width: '100%', padding: '8px 10px', fontSize: 14, border: '0.5px solid #B4B2A9', borderRadius: 6, boxSizing: 'border-box' };
  const labelStyle = { fontSize: 13, color: colors.textMuted, display: 'block', marginBottom: 4 };

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/listings/create/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ ...form, latitude, longitude }) });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not create listing.');

      const token = getAuthToken();
      if (imageFile) {
        const fd = new FormData();
        fd.append('image', imageFile);
        await fetch(`${API_BASE}/listings/${data.id}/upload-image/`, { method: 'POST', headers: token ? { Authorization: `Token ${token}` } : {}, body: fd });
      }
      for (const gf of galleryFiles) {
        const fd = new FormData();
        fd.append('image', gf);
        await fetch(`${API_BASE}/listings/${data.id}/gallery/upload/`, { method: 'POST', headers: token ? { Authorization: `Token ${token}` } : {}, body: fd });
      }
      alert('Listing created!');
      setForm({ title: '', property_type: 'apartment', description: '', price_per_night_pi: '', city: '', country: '', address: '', max_guests: 2, bedrooms: 1, bathrooms: 1, amenities: '', cancellation_policy: 'moderate', weekly_discount_percent: 0, monthly_discount_percent: 0, check_in_time: '3:00 PM', check_out_time: '11:00 AM', smoking_allowed: false, pets_allowed: false, parties_allowed: false, house_rules: '' });
      setImageFile(null);
      setGalleryFiles([]);
      setLatitude(null);
      setLongitude(null);
      onCreated();
    } catch (e) {
      alert(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ background: '#F1EFE8', border: `0.5px solid ${colors.border}`, borderRadius: 12, padding: '1.25rem' }}>
      <p style={{ fontWeight: 500, fontSize: 15, margin: '0 0 12px' }}>{t('list_your_place')}</p>
      <div style={{ display: 'grid', gap: 10 }}>
        <div>
          <label style={labelStyle}>{t('title_label')}</label>
          <input style={inputStyle} value={form.title} onChange={update('title')} placeholder="Cozy 2BR apartment near the beach" />
        </div>
        <div>
          <label style={labelStyle}>{t('property_type')}</label>
          <select style={inputStyle} value={form.property_type} onChange={update('property_type')}>
            {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>{t('description')}</label>
          <textarea style={{ ...inputStyle, resize: 'vertical' }} rows={3} value={form.description} onChange={update('description')} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>{t('tv_price_night')}</label><input style={inputStyle} type="number" step="0.01" value={form.price_per_night_pi} onChange={update('price_per_night_pi')} /></div>
          <div><label style={labelStyle}>{t('max_guests')}</label><input style={inputStyle} type="number" value={form.max_guests} onChange={update('max_guests')} /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>{t('bedrooms')}</label><input style={inputStyle} type="number" value={form.bedrooms} onChange={update('bedrooms')} /></div>
          <div><label style={labelStyle}>{t('bathrooms')}</label><input style={inputStyle} type="number" value={form.bathrooms} onChange={update('bathrooms')} /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>{t('city')}</label><input style={inputStyle} value={form.city} onChange={update('city')} /></div>
          <div><label style={labelStyle}>{t('country')}</label><input style={inputStyle} value={form.country} onChange={update('country')} /></div>
        </div>
        <div>
          <label style={labelStyle}>{t('address')}</label>
          <input style={inputStyle} value={form.address} onChange={update('address')} />
        </div>
        <div>
          <button type="button" onClick={() => setShowMapPicker(true)} style={{ padding: '8px 14px', fontSize: 13, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primaryBg, color: colors.primary, cursor: 'pointer', marginBottom: 12 }}>
            🗺️ Pin exact location on map {latitude ? '✓ Set' : ''}
          </button>
          {showMapPicker && (
            <MapPickerModal
              initialLat={latitude}
              initialLng={longitude}
              onClose={() => setShowMapPicker(false)}
              onConfirm={({ latitude: lat, longitude: lng }) => { setLatitude(lat); setLongitude(lng); setShowMapPicker(false); }}
            />
          )}
        </div>
        <div>
          <label style={labelStyle}>{t('tv_amenities')}</label>
          <input style={inputStyle} value={form.amenities} onChange={update('amenities')} placeholder="WiFi, AC, Parking, Kitchen" />
        </div>
        <div>
          <label style={labelStyle}>{t('cancellation_policy')}</label>
          <select style={inputStyle} value={form.cancellation_policy} onChange={update('cancellation_policy')}>
            <option value="flexible">{t('tv_flex')}</option>
            <option value="moderate">{t('tv_moderate')}</option>
            <option value="strict">{t('tv_strict')}</option>
          </select>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>{t('tv_weekly')}</label><input type="number" style={inputStyle} value={form.weekly_discount_percent} onChange={update('weekly_discount_percent')} /></div>
          <div><label style={labelStyle}>{t('tv_monthly')}</label><input type="number" style={inputStyle} value={form.monthly_discount_percent} onChange={update('monthly_discount_percent')} /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>{t('check_in_time')}</label><input style={inputStyle} value={form.check_in_time} onChange={update('check_in_time')} placeholder="3:00 PM" /></div>
          <div><label style={labelStyle}>{t('check_out_time')}</label><input style={inputStyle} value={form.check_out_time} onChange={update('check_out_time')} placeholder="11:00 AM" /></div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>{t('house_rules')}</label>
          <div style={{ display: 'flex', gap: 14, marginBottom: 8, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
              <input type="checkbox" checked={form.smoking_allowed} onChange={(e) => setForm((f) => ({ ...f, smoking_allowed: e.target.checked }))} /> 🚬 Smoking allowed
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
              <input type="checkbox" checked={form.pets_allowed} onChange={(e) => setForm((f) => ({ ...f, pets_allowed: e.target.checked }))} /> 🐾 Pets allowed
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
              <input type="checkbox" checked={form.parties_allowed} onChange={(e) => setForm((f) => ({ ...f, parties_allowed: e.target.checked }))} /> 🎉 Parties allowed
            </label>
          </div>
          <textarea style={{ ...inputStyle, resize: 'vertical' }} rows={2} placeholder={t('house_rules')} value={form.house_rules} onChange={update('house_rules')} />
        </div>
        <div>
          <label style={labelStyle}>{t('cover_photo')}</label>
          <input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} />
        </div>
        <div>
          <label style={labelStyle}>{t('tv_gallery')}</label>
          <input type="file" accept="image/*" multiple onChange={(e) => setGalleryFiles(Array.from(e.target.files))} />
          {galleryFiles.length > 0 && <p style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>{galleryFiles.length} photo(s) selected</p>}
        </div>
      </div>
      <button onClick={submit} disabled={submitting || !form.title || !form.price_per_night_pi} style={{ width: '100%', padding: '10px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer', marginTop: 12 }}>
        {submitting ? t('creating') : t('publish_listing')}
      </button>
    </div>
  );
}

function CalendarManager({ listingId, onClose }) {
  const [blockedDates, setBlockedDates] = useState([]);
  const [newDate, setNewDate] = useState('');

  const load = useCallback(async () => {
    const res = await fetch(`${API_BASE}/listings/${listingId}/blocked-dates/`, { headers: authHeaders() });
    if (res.ok) setBlockedDates((await safeJson(res)).blocked_dates);
  }, [listingId]);

  useEffect(() => { load(); }, [load]);

  const blockDate = async () => {
    if (!newDate) return;
    await fetch(`${API_BASE}/listings/${listingId}/blocked-dates/update/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ dates: [newDate], action: 'block' }) });
    setNewDate('');
    load();
  };

  const unblockDate = async (date) => {
    await fetch(`${API_BASE}/listings/${listingId}/blocked-dates/update/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ dates: [date], action: 'unblock' }) });
    load();
  };

  return (
    <div style={{ background: '#fff', border: `0.5px solid ${colors.border}`, borderRadius: 8, padding: 10, marginTop: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <p style={{ fontSize: 12, fontWeight: 600, margin: 0 }}>{t('blocked_dates')}</p>
        <button onClick={onClose} style={{ fontSize: 11, border: 'none', background: 'none', color: colors.textMuted, cursor: 'pointer' }}>{t('close')}</button>
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        <input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} style={{ flex: 1, padding: '6px', fontSize: 12, border: `0.5px solid ${colors.border}`, borderRadius: 6 }} />
        <button onClick={blockDate} style={{ padding: '6px 12px', fontSize: 11, border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('block')}</button>
      </div>
      {blockedDates.length === 0 ? <p style={{ fontSize: 11, color: colors.textMuted }}>{t('no_dates_blocked')}</p> : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {blockedDates.map((d) => (
            <span key={d} onClick={() => unblockDate(d)} style={{ fontSize: 11, background: '#FBEAE3', color: colors.danger, padding: '3px 8px', borderRadius: 8, cursor: 'pointer' }}>{d} ✕</span>
          ))}
        </div>
      )}
    </div>
  );
}

function MyListingsPanel() {
  const [listings, setListings] = useState(null);
  const [boosting, setBoosting] = useState({});
  const [calendarFor, setCalendarFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/listings/mine/`, { headers: authHeaders() });
      if (res.ok) setListings((await safeJson(res)).listings);
    } catch (e) {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const boostListing = async (id, title) => {
    if (!window.Pi) { alert('Open inside Pi Browser to pay with Pi.'); return; }
    setBoosting((b) => ({ ...b, [id]: true }));
    try {
      await window.Pi.authenticate(['username', 'payments'], () => {});
      window.Pi.createPayment(
        { amount: 1, memo: `Boost listing: ${title}`, metadata: { listing_id: id } },
        {
          onReadyForServerApproval: (paymentId) => fetch(`${API_BASE}/listings/${id}/boost/approve/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ payment_id: paymentId }) }),
          onReadyForServerCompletion: (paymentId, txid) => {
            fetch(`${API_BASE}/listings/${id}/boost/complete/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ payment_id: paymentId, txid, amount: 1, days: 7 }) })
              .then(() => { alert('Boosted for 7 days!'); setBoosting((b) => ({ ...b, [id]: false })); load(); })
              .catch(() => { alert('Boost failed.'); setBoosting((b) => ({ ...b, [id]: false })); });
          },
          onCancel: () => setBoosting((b) => ({ ...b, [id]: false })),
          onError: () => setBoosting((b) => ({ ...b, [id]: false })),
        }
      );
    } catch (e) {
      setBoosting((b) => ({ ...b, [id]: false }));
    }
  };

  return (
    <div>
      {listings && listings.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginBottom: '1.5rem' }}>
          {listings.map((l) => (
            <div key={l.id} style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 10, padding: 8 }}>
              <div style={{ width: '100%', aspectRatio: '4/3', background: '#F1EFE8', borderRadius: 6, marginBottom: 6, overflow: 'hidden' }}>
                {l.cover_image && <img src={l.cover_image} alt={l.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
              </div>
              <p style={{ fontSize: 12, fontWeight: 500, margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.title}</p>
              <p style={{ fontSize: 11, color: colors.textMuted, margin: '0 0 6px' }}>{l.bookings_count} bookings · <PiPrice pi={l.price_per_night_pi} />/night</p>
              <button onClick={() => boostListing(l.id, l.title)} disabled={boosting[l.id] || l.is_boosted} style={{ width: '100%', padding: '4px', fontSize: 11, fontWeight: 500, border: 'none', borderRadius: 4, background: l.is_boosted ? '#FFF3C4' : colors.primaryBg, color: l.is_boosted ? '#8A6D00' : colors.primary, cursor: l.is_boosted ? 'default' : 'pointer' }}>
                {l.is_boosted ? '📢 Boosted' : boosting[l.id] ? 'Processing...' : '📢 Boost (1 Pi / 7d)'}
              </button>
              <button onClick={() => setCalendarFor(calendarFor === l.id ? null : l.id)} style={{ width: '100%', padding: '4px', fontSize: 11, marginTop: 4, border: `0.5px solid ${colors.border}`, borderRadius: 4, background: '#fff', cursor: 'pointer' }}>
                📅 Manage Calendar
              </button>
              {calendarFor === l.id && <CalendarManager listingId={l.id} onClose={() => setCalendarFor(null)} />}
            </div>
          ))}
        </div>
      )}
      <CreateListingForm onCreated={load} />
    </div>
  );
}

function BookingsPanel() {
  const [view, setView] = useState('guest');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = view === 'guest' ? 'bookings/mine/' : 'bookings/host/';
      const res = await fetch(`${API_BASE}/${endpoint}`, { headers: authHeaders() });
      if (res.ok) setBookings((await safeJson(res)).bookings);
    } catch (e) {} finally { setLoading(false); }
  }, [view]);

  useEffect(() => { load(); }, [load]);

  const cancelBooking = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/bookings/${id}/cancel/`, { method: 'POST', headers: authHeaders() });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not cancel.');
      alert(`Booking cancelled. You're entitled to a ${data.refund_percent}% refund ($<PiPrice pi={data.refund_amount_pi} />) per the cancellation policy. Refunds are processed manually by an admin.`);
      load();
    } catch (e) { alert(networkMessage(e)); }
  };

  const leaveReview = async (id) => {
    const rating = parseInt(prompt('Rate 1-5:', '5'), 10);
    if (!rating) return;
    const comment = prompt('Comment (optional):', '') || '';
    try {
      const res = await fetch(`${API_BASE}/bookings/${id}/review/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ rating, comment }) });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not submit review.');
      alert('Thanks for your review!');
    } catch (e) { alert(networkMessage(e)); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button onClick={() => setView('guest')} style={{ padding: '4px 12px', fontSize: 12, borderRadius: 14, border: `0.5px solid ${colors.border}`, background: view === 'guest' ? colors.primaryBg : '#fff', color: view === 'guest' ? colors.primary : colors.textMuted, cursor: 'pointer' }}>{t('my_trips')}</button>
        <button onClick={() => setView('host')} style={{ padding: '4px 12px', fontSize: 12, borderRadius: 14, border: `0.5px solid ${colors.border}`, background: view === 'host' ? colors.primaryBg : '#fff', color: view === 'host' ? colors.primary : colors.textMuted, cursor: 'pointer' }}>{t('host_bookings')}</button>
        <button onClick={load} style={{ marginLeft: 'auto', fontSize: 12, padding: '4px 10px', border: `0.5px solid ${colors.border}`, borderRadius: 6, background: '#fff', cursor: 'pointer' }}>🔄</button>
      </div>

      {loading ? <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('loading')}</p> :
        bookings.length === 0 ? <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('no_bookings_yet')}</p> :
        bookings.map((b) => (
          <div key={b.id} style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 10, padding: '0.75rem', marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 13, fontWeight: 500 }}>{b.listing_title} · {b.tracking_code}</span>
              <span style={{ fontSize: 12, color: colors.textMuted, textTransform: 'capitalize' }}>{b.status}</span>
            </div>
            <p style={{ fontSize: 12, color: colors.textMuted, margin: '2px 0' }}>
              {view === 'guest' ? `Host: @${b.host_username}` : `Guest: @${b.guest_username} · ${b.guest_phone}`} · {b.check_in} → {b.check_out} ({b.nights}n) · <PiPrice pi={b.price_pi} />
            </p>
            {view === 'host' && b.notes && <p style={{ fontSize: 12, background: '#F1EFE8', padding: 6, borderRadius: 6, margin: '4px 0' }}>{b.notes}</p>}
            {view === 'host' && b.status === 'paid' && <p style={{ fontSize: 12, color: colors.primary, margin: '4px 0' }}>Net payout: <PiPrice pi={b.net_payout_pi} /></p>}
            {view === 'guest' && (b.status === 'pending' || b.status === 'paid') && (
              <button onClick={() => cancelBooking(b.id)} style={{ fontSize: 11, padding: '4px 10px', border: `0.5px solid ${colors.border}`, borderRadius: 10, background: '#fff', cursor: 'pointer', marginTop: 6 }}>{t('cancel')}</button>
            )}
            {view === 'guest' && b.status === 'paid' && (
              <button onClick={() => leaveReview(b.id)} style={{ fontSize: 11, padding: '4px 10px', border: `0.5px solid ${colors.border}`, borderRadius: 10, background: '#fff', cursor: 'pointer', marginTop: 6, marginLeft: 6 }}>⭐ {t('tv_review')}</button>
            )}
          </div>
        ))
      }
    </div>
  );
}

function WalletPanel() {
  const [balance, setBalance] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [showLedger, setShowLedger] = useState(false);
  const [amount, setAmount] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/wallet/balance/`, { headers: authHeaders() });
      if (res.ok) setBalance(await safeJson(res));
    } catch (e) {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleLedger = async () => {
    if (!showLedger) {
      try {
        const res = await fetch(`${API_BASE}/wallet/ledger/`, { headers: authHeaders() });
        if (res.ok) setLedger((await safeJson(res)).entries);
      } catch (e) {}
    }
    setShowLedger((s) => !s);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/wallet/withdraw/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ amount: parseFloat(amount), pi_wallet_address: walletAddress }) });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not submit request.');
      alert('Withdrawal request submitted.');
      setAmount(''); setWalletAddress('');
      load();
    } catch (e) { alert(networkMessage(e)); } finally { setSubmitting(false); }
  };

  if (balance === null) return <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('loading_wallet')}</p>;

  return (
    <div>
      <div style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 12, padding: '1rem', marginBottom: '1.5rem' }}>
        <p style={{ fontSize: 13, color: colors.textMuted, margin: '0 0 4px' }}>{t('available_to_withdraw')}</p>
        <p style={{ fontSize: 26, fontWeight: 600, margin: '0 0 4px', color: colors.primary }}><PiPrice pi={balance.available_balance_pi} /></p>
        <p style={{ fontSize: 12, color: colors.textMuted, margin: '0 0 8px' }}>Total earned (after 5% fee): <PiPrice pi={balance.total_earned_pi} /></p>
        <button onClick={toggleLedger} style={{ fontSize: 12, padding: '4px 10px', border: `0.5px solid ${colors.border}`, borderRadius: 6, background: '#fff', cursor: 'pointer' }}>{showLedger ? 'Hide' : 'View'} ledger</button>
        {showLedger && (
          <div style={{ marginTop: 10, borderTop: `0.5px solid ${colors.border}`, paddingTop: 10 }}>
            {ledger.length === 0 ? <p style={{ fontSize: 12, color: colors.textMuted }}>{t('no_completed_bookings')}</p> :
              ledger.map((e) => (
                <div key={e.booking_id} style={{ fontSize: 12, padding: '6px 0', borderBottom: `0.5px solid ${colors.border}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>{e.listing_title} · @{e.guest}</span>
                    <span><PiPrice pi={e.net_pi} /> net</span>
                  </div>
                </div>
              ))
            }
          </div>
        )}
      </div>

      <div style={{ background: '#F1EFE8', border: `0.5px solid ${colors.border}`, borderRadius: 12, padding: '1.25rem' }}>
        <p style={{ fontWeight: 500, fontSize: 14, margin: '0 0 12px' }}>{t('request_withdrawal')}</p>
        <div style={{ display: 'grid', gap: 10, marginBottom: 10 }}>
          <input type="number" step="0.000001" placeholder={t('amount_pi')} value={amount} onChange={(e) => setAmount(e.target.value)} style={{ padding: '8px 10px', fontSize: 14, border: '0.5px solid #B4B2A9', borderRadius: 6 }} />
          <input type="text" placeholder={t('your_wallet_address')} value={walletAddress} onChange={(e) => setWalletAddress(e.target.value)} style={{ padding: '8px 10px', fontSize: 14, border: '0.5px solid #B4B2A9', borderRadius: 6 }} />
        </div>
        <button onClick={submit} disabled={submitting || !amount || !walletAddress} style={{ width: '100%', padding: '10px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer' }}>
          {submitting ? t('tv_submitting') : t('tv_req_wd')}
        </button>
      </div>
    </div>
  );
}

function TrackPanel() {
  const [code, setCode] = useState('');
  const [result, setResult] = useState(null);
  const [msg, setMsg] = useState('');

  const track = async () => {
    setMsg(''); setResult(null);
    try {
      const res = await fetch(`${API_BASE}/track/${encodeURIComponent(code)}/`);
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Booking not found.');
      setResult(data);
    } catch (e) { setMsg(e.message); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input placeholder={t('enter_tracking_code') + ' (e.g. TR000001)'} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} style={{ flex: 1, padding: '10px 14px', fontSize: 14, border: '0.5px solid #B4B2A9', borderRadius: 20 }} />
        <button onClick={track} style={{ padding: '0 20px', fontSize: 13, fontWeight: 500, border: 'none', borderRadius: 20, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('tv_track')}</button>
      </div>
      {msg && <p style={{ color: colors.danger, fontSize: 13 }}>{msg}</p>}
      {result && (
        <div style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 12, padding: '1rem' }}>
          <p style={{ fontSize: 13, color: colors.textMuted, margin: '0 0 4px' }}>{result.listing_title} · {result.city}</p>
          <p style={{ fontSize: 20, fontWeight: 600, margin: '0 0 8px', color: colors.primary, textTransform: 'capitalize' }}>{result.status}</p>
          <p style={{ fontSize: 13 }}>{result.check_in} → {result.check_out}</p>
        </div>
      )}
    </div>
  );
}

function TravelHelpModal({ onClose }) {
  const sectionStyle = { marginBottom: 20 };
  const headingStyle = { fontSize: 16, fontWeight: 600, margin: '0 0 8px' };
  const textStyle = { fontSize: 14, color: '#3A3935', lineHeight: 1.6, margin: '0 0 8px' };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px 12px', overflowY: 'auto' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, maxWidth: 640, width: '100%', padding: '1.5rem', marginTop: 20 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 20 }}>{t('tvh_title')}</h2>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: colors.textMuted }}>✕</button>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>What is this?</p>
          <p style={textStyle}>{t('tvh_what')}</p>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>{t('tvh_book_q')}</p>
          <p style={textStyle}>{t('tvh_b1')}</p>
          <p style={textStyle}>{t('tvh_b2')}</p>
          <p style={textStyle}>{t('tvh_b3')}</p>
          <p style={textStyle}>{t('tvh_b4')}</p>
          <p style={textStyle}>{t('tvh_b5')}</p>
          <p style={textStyle}>{t('tvh_b6')}</p>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>{t('tvh_host_q')}</p>
          <p style={textStyle}>{t('tvh_h1')}</p>
          <p style={textStyle}>{t('tvh_h2')}</p>
          <p style={textStyle}>{t('tvh_h3')}</p>
          <p style={textStyle}>{t('tvh_h4')}</p>
          <p style={textStyle}>{t('tvh_h5')}</p>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>{t('tvh_fees_q')}</p>
          <p style={textStyle}>{t('tvh_fees')}</p>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>{t('tvh_faq_q')}</p>
          <p style={{ ...textStyle, fontWeight: 600, marginBottom: 2 }}>{t('tvh_q1')}</p>
          <p style={textStyle}>{t('tvh_a1')}</p>
          <p style={{ ...textStyle, fontWeight: 600, marginBottom: 2 }}>{t('tvh_q2')}</p>
          <p style={textStyle}>{t('tvh_a2')}</p>
          <p style={{ ...textStyle, fontWeight: 600, marginBottom: 2 }}>{t('tvh_q3')}</p>
          <p style={textStyle}>{t('tvh_a3')}</p>
        </div>

        <button onClick={onClose} style={{ width: '100%', padding: '10px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>
          Got it
        </button>
      </div>
    </div>
  );
}

export default function TravelMarketplace() {
  const [tab, setTab] = useState('browse');
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [personalized, setPersonalized] = useState(false);
  const [openSlug, setOpenSlug] = useState(null);
  const [piRate, setPiRate] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  const loadListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (selectedType) params.set('property_type', selectedType);
      if (cityFilter) params.set('city', cityFilter);
      const res = await fetch(`${API_BASE}/listings/?${params.toString()}`, { headers: getAuthToken() ? authHeaders() : {} });
      const data = await safeJson(res);
      setListings(data.listings || []);
      setPersonalized(!!data.personalized);
    } catch (e) {} finally { setLoading(false); }
  }, [search, selectedType, cityFilter]);

  const loadCategories = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/listings/categories/`);
      if (res.ok) setCategories((await safeJson(res)).categories);
    } catch (e) {}
  }, []);

  useEffect(() => { loadListings(); }, [loadListings]);
  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => {
    fetch('https://api.coingecko.com/api/v3/simple/price?ids=pi-network&vs_currencies=usd')
      .then((r) => r.json())
      .then((data) => { if (data['pi-network']?.usd) setPiRate(data['pi-network'].usd); })
      .catch(() => {});
  }, []);

  const tabStyle = (t) => ({ padding: '8px 14px', fontSize: 13, fontWeight: 500, border: 'none', borderBottom: tab === t ? `2px solid ${colors.primary}` : '2px solid transparent', background: 'transparent', color: tab === t ? colors.primary : colors.textMuted, cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' });

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '1.5rem', fontFamily: 'sans-serif', background: colors.bg, minHeight: '100vh' }}>
      {showHelp && <TravelHelpModal onClose={() => setShowHelp(false)} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ fontSize: 22, margin: '0 0 4px' }}>🏨 {t('tv_app')}</h1>
          <p style={{ fontSize: 13, color: colors.textMuted, margin: '0 0 1rem' }}>{t('travel_tagline')}</p>
        </div>
        <button onClick={() => setShowHelp(true)} style={{ padding: '6px 14px', fontSize: 13, fontWeight: 500, border: `0.5px solid ${colors.border}`, borderRadius: 20, background: '#fff', cursor: 'pointer', flexShrink: 0 }}>
          ❓ {t('how_it_works')}
        </button>
      </div>

      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', borderBottom: `0.5px solid ${colors.border}`, marginBottom: '1.25rem' }}>
        <button style={tabStyle('browse')} onClick={() => setTab('browse')}>{t('browse')}</button>
        <button style={tabStyle('map')} onClick={() => setTab('map')}>🗺️ {t('tv_map')}</button>
        <button style={tabStyle('listings')} onClick={() => setTab('listings')}>{t('my_listings')}</button>
        <button style={tabStyle('bookings')} onClick={() => setTab('bookings')}>{t('bookings')}</button>
        <button style={tabStyle('wallet')} onClick={() => setTab('wallet')}>{t('wallet')}</button>
        <button style={tabStyle('track')} onClick={() => setTab('track')}>{t('track')}</button>
      </div>

      {tab === 'map' && <TravelMapView onOpenListing={(slug) => setOpenSlug(slug)} />}

      {tab === 'browse' && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: '0.75rem' }}>
            <input placeholder={t('tv_search_ph')} value={search} onChange={(e) => setSearch(e.target.value)} style={{ flex: 1, padding: '10px 14px', fontSize: 14, border: `0.5px solid #B4B2A9`, borderRadius: 20, boxSizing: 'border-box' }} />
            <input placeholder={t('tv_city_ph')} value={cityFilter} onChange={(e) => setCityFilter(e.target.value)} style={{ width: 110, padding: '10px 14px', fontSize: 14, border: `0.5px solid #B4B2A9`, borderRadius: 20, boxSizing: 'border-box' }} />
            <button onClick={loadListings} style={{ padding: '0 14px', fontSize: 13, border: `0.5px solid ${colors.border}`, borderRadius: 20, background: '#fff', cursor: 'pointer' }}>🔄</button>
          </div>

          {categories.length > 0 && (
            <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8, marginBottom: '0.5rem' }}>
              <button onClick={() => setSelectedType('')} style={{ flexShrink: 0, padding: '5px 12px', fontSize: 12, borderRadius: 14, whiteSpace: 'nowrap', border: `0.5px solid ${colors.border}`, cursor: 'pointer', background: selectedType === '' ? colors.primary : '#fff', color: selectedType === '' ? '#fff' : colors.textMuted }}>{t('all_filter')}</button>
              {categories.map((c) => (
                <button key={c.name} onClick={() => setSelectedType(c.name)} style={{ flexShrink: 0, padding: '5px 12px', fontSize: 12, borderRadius: 14, whiteSpace: 'nowrap', border: `0.5px solid ${colors.border}`, cursor: 'pointer', background: selectedType === c.name ? colors.primary : '#fff', color: selectedType === c.name ? '#fff' : colors.textMuted }}>{c.name} ({c.count})</button>
              ))}
            </div>
          )}

          {personalized && <p style={{ fontSize: 12, color: colors.primary, margin: '0 0 0.75rem' }}>✨ {t('tv_sorted')}</p>}

          {loading ? <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('loading_stays')}</p> :
            listings.length === 0 ? <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('no_stays_found')}</p> : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
                {listings.map((l) => <ListingCard key={l.id} listing={l} onOpen={setOpenSlug} piRate={piRate} />)}
              </div>
            )
          }

          {openSlug && <ListingDetailModal slug={openSlug} onClose={() => setOpenSlug(null)} onBooked={() => setOpenSlug(null)} />}
        </>
      )}

      {tab === 'listings' && <MyListingsPanel />}
      {tab === 'bookings' && <BookingsPanel />}
      {tab === 'wallet' && <WalletPanel />}
      {tab === 'track' && <TrackPanel />}
    </div>
  );
}
